import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend
} from 'recharts';
import api from '../services/api';
import {
  ArtworkMetrics,
  ArtworkOptionMetrics,
  ArtistMetricsDto,
  ArtistMetricsHistoryPoint
} from '../types';
import { useToast } from '../contexts/ToastContext';
import './ArtistMetrics.css';

const ArtistMetrics: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [tierMetrics, setTierMetrics] = useState<ArtistMetricsDto | null>(null);
  const [metricsHistory, setMetricsHistory] = useState<ArtistMetricsHistoryPoint[]>([]);
  const [artworkTotals, setArtworkTotals] = useState<ArtworkMetrics[]>([]);
  const [optionTotals, setOptionTotals] = useState<ArtworkOptionMetrics[]>([]);
  const [myArtworks, setMyArtworks] = useState<{ id: number; title: string }[]>([]);
  const [featuredIds, setFeaturedIds] = useState<number[]>([]);
  const [maxFeatured, setMaxFeatured] = useState<number>(3);
  const [saving, setSaving] = useState<boolean>(false);
  const { success, error: showError } = useToast();

  useEffect(() => {
    const fetchMetrics = async (): Promise<void> => {
      try {
        const tierPromise = api.get<ArtistMetricsDto>('/artist/metrics', { validateStatus: (s) => s < 500 });
        const historyPromise = api.get<ArtistMetricsHistoryPoint[]>('/artist/metrics/history', { validateStatus: (s) => s < 500 });
        const metricsPromise = api.get<{ artworkTotals: ArtworkMetrics[]; optionTotals: ArtworkOptionMetrics[] }>('/metrics/artist');
        const featuredPromise = api.get<number[]>('/artist/featured');
        const maxPromise = api.get<{ key: string; value: string | null }>('/admin/settings/ArtistFeaturedMax', { validateStatus: (s) => s < 500 });

        const [tierRes, historyRes, metricsRes, featuredRes, maxRes] = await Promise.all([
          tierPromise,
          historyPromise,
          metricsPromise.catch(() => ({ data: { artworkTotals: [], optionTotals: [] } })),
          featuredPromise.catch(() => ({ data: [] as number[] })),
          maxPromise
        ]);

        if (tierRes.status === 200 && tierRes.data) {
          setTierMetrics(tierRes.data);
        } else {
          setTierMetrics(null);
        }
        if (historyRes.status === 200 && Array.isArray(historyRes.data)) {
          setMetricsHistory(historyRes.data);
        }
        const m = metricsRes.data;
        setArtworkTotals(m?.artworkTotals || []);
        setOptionTotals(m?.optionTotals || []);
        setMyArtworks((m?.artworkTotals || []).map((a: ArtworkMetrics) => ({ id: a.artworkId, title: a.title })));
        setFeaturedIds(Array.isArray(featuredRes.data) ? featuredRes.data : []);
        if (maxRes.status === 200 && maxRes.data?.value) {
          setMaxFeatured(parseInt(maxRes.data.value));
        } else {
          setMaxFeatured(3);
        }
      } catch (error) {
        console.error('Failed to fetch metrics:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchMetrics();
  }, []);

  const toggleFeatured = (id: number): void => {
    if (featuredIds.includes(id)) {
      setFeaturedIds(featuredIds.filter(x => x !== id));
    } else if (featuredIds.length < maxFeatured) {
      setFeaturedIds([...featuredIds, id]);
    }
  };

  const saveFeatured = async (): Promise<void> => {
    setSaving(true);
    try {
      await api.put('/artist/featured', featuredIds);
      success('Featured artworks updated');
    } catch (error) {
      showError('Failed to update featured artworks');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="loading">Loading artist metrics...</div>;
  }

  const tierBadgeClass = tierMetrics?.currentTier ? `tier-badge tier-${tierMetrics.currentTier.toLowerCase()}` : 'tier-badge';

  return (
    <div className="artist-metrics">
      <div className="container">
        <h1>My Artwork Metrics</h1>

        {tierMetrics && (
          <div className="metrics-card tier-dashboard">
            <h2>Artist tier &amp; earnings</h2>
            <div className="tier-dashboard-row">
              <div className="tier-current">
                <span className={tierBadgeClass}>{tierMetrics.currentTier}</span>
                <p className="tier-stats">
                  Lifetime earnings: <strong>${tierMetrics.totalLifetimeEarnings.toLocaleString('en-US', { minimumFractionDigits: 2 })}</strong>
                  {' · '}Orders: <strong>{tierMetrics.orderCount}</strong>
                  {' · '}Artworks: <strong>{tierMetrics.artworkCount}</strong>
                </p>
                <p className="tier-benefits">
                  Your benefits: <strong>{tierMetrics.currentTierDiscountPercent}%</strong> discount on your own orders,{' '}
                  <strong>{tierMetrics.currentTierUploadLimit}</strong> artwork slots.
                </p>
              </div>
            </div>
            {tierMetrics.nextTier && (
              <div className="tier-progress">
                <p>Progress to {tierMetrics.nextTier.name}: ${tierMetrics.currentEarningsInTier.toFixed(0)} of ${tierMetrics.earningsNeededForNextTier.toFixed(0)} needed</p>
                <div className="progress-bar-wrap">
                  <div
                    className="progress-bar-fill"
                    style={{ width: `${Math.min(100, tierMetrics.progressToNextTierPercent)}%` }}
                  />
                </div>
              </div>
            )}
            {!tierMetrics.nextTier && <p className="tier-max">You&apos;re at the top tier (Elite).</p>}
          </div>
        )}

        {tierMetrics && metricsHistory.length > 0 && (
          <div className="metrics-card">
            <h2>Earnings over time</h2>
            <div className="chart-wrap">
              <ResponsiveContainer width="100%" height={280}>
                <LineChart data={metricsHistory} margin={{ top: 10, right: 20, left: 10, bottom: 10 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--border-color, #eee)" />
                  <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                  <YAxis tick={{ fontSize: 12 }} tickFormatter={(v) => `$${v}`} />
                  <Tooltip
                    formatter={(value: number, name: string) => [name === 'cumulativeEarnings' ? `$${Number(value).toLocaleString()}` : `$${Number(value).toLocaleString()}`, name === 'cumulativeEarnings' ? 'Cumulative' : 'That month']}
                    labelFormatter={(label) => `Month: ${label}`}
                  />
                  <Legend />
                  <Line type="monotone" dataKey="cumulativeEarnings" name="Cumulative earnings" stroke="var(--color-primary, #6366f1)" strokeWidth={2} dot={false} />
                  <Line type="monotone" dataKey="earningsThatMonth" name="Earnings that month" stroke="var(--color-success, #22c55e)" strokeWidth={1.5} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}

        {!tierMetrics && (
          <div className="metrics-card">
            <p>You need an approved artist profile to see tier and earnings. <Link to="/become-an-artist">Learn about the Phi Artist program</Link>.</p>
          </div>
        )}

        <div className="metrics-card">
          <h2>Artwork Totals</h2>
          {artworkTotals.length === 0 ? (
            <p>No metrics yet.</p>
          ) : (
            <table className="metrics-table">
              <thead>
                <tr>
                  <th>Artwork</th>
                  <th>Viewed</th>
                  <th>Selected</th>
                  <th>Carted</th>
                  <th>Ordered</th>
                </tr>
              </thead>
              <tbody>
                {artworkTotals.map((item) => (
                  <tr key={item.artworkId}>
                    <td>{item.title}</td>
                    <td>{item.viewed}</td>
                    <td>{item.selected}</td>
                    <td>{item.carted}</td>
                    <td>{item.ordered}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className="metrics-card">
          <h2>Featured Works (max {maxFeatured})</h2>
          {myArtworks.length === 0 ? (
            <p>No artworks to feature yet.</p>
          ) : (
            <div className="featured-list">
              {myArtworks.map((art) => (
                <label key={art.id} className="featured-item">
                  <input
                    type="checkbox"
                    checked={featuredIds.includes(art.id)}
                    onChange={() => toggleFeatured(art.id)}
                    disabled={!featuredIds.includes(art.id) && featuredIds.length >= maxFeatured}
                  />
                  <span>{art.title}</span>
                </label>
              ))}
            </div>
          )}
          <button className="btn btn-primary" onClick={saveFeatured} disabled={saving}>
            {saving && <span className="btn-spinner" aria-hidden="true"></span>}
            Save Featured Works
          </button>
        </div>

        <div className="metrics-card">
          <h2>Artwork Options</h2>
          {optionTotals.length === 0 ? (
            <p>No option metrics yet.</p>
          ) : (
            <table className="metrics-table">
              <thead>
                <tr>
                  <th>Artwork ID</th>
                  <th>Size</th>
                  <th>Viewed</th>
                  <th>Selected</th>
                  <th>Carted</th>
                  <th>Ordered</th>
                </tr>
              </thead>
              <tbody>
                {optionTotals.map((item, index) => (
                  <tr key={`${item.artworkId}-${item.size}-${index}`}>
                    <td>{item.artworkId}</td>
                    <td>{item.size || '—'}</td>
                    <td>{item.viewed}</td>
                    <td>{item.selected}</td>
                    <td>{item.carted}</td>
                    <td>{item.ordered}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};

export default ArtistMetrics;
